import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { api, ApiError } from '../api/client';
import './DomainAddRuns.css';

type RunStatus = 'queued' | 'running' | 'completed' | 'failed';
type ItemStatus = 'pending' | 'running' | 'added' | 'already' | 'error';

interface RunInfo {
  id: string;
  status: RunStatus;
  panels_total: number;
  panels_done: number;
  panels_errored: number;
  panels_already: number;
  auto_retry_enabled: boolean;
  auto_retry_attempts_used: number;
  auto_retry_attempts_cap: number;
  triggered_by: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

interface RunItem {
  panel_email: string;
  new_domain: string;
  source: 'panel' | 'inventory_serken' | 'inventory_regular';
  status: ItemStatus;
  error_code: string | null;
  error_message: string | null;
}

interface CreateRunResp {
  run_id: string;
  items_enqueued: { panel_email: string; new_domain: string; source: string }[];
  skipped: { email: string; reason: string }[];
}

interface Toast {
  type: 'success' | 'error' | 'info';
  message: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DOMAIN_RE = /^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/;
const SOURCE_LABEL: Record<RunItem['source'], string> = {
  panel: 'Panel',
  inventory_serken: 'Serken',
  inventory_regular: 'Regular',
};

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Mirror of the server-side parser, for live feedback only — the server
 *  remains the authority. */
function countPairs(text: string): { valid: number; invalid: number } {
  let valid = 0;
  let invalid = 0;
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (line === '' || line.startsWith('#')) continue;
    const tokens = line.split(/[\s,;]+/).filter(Boolean);
    if (
      tokens.length === 2 &&
      EMAIL_RE.test(tokens[0]!.toLowerCase()) &&
      DOMAIN_RE.test(tokens[1]!.toLowerCase())
    ) {
      valid++;
    } else {
      invalid++;
    }
  }
  return { valid, invalid };
}

export function DomainAddRuns(): JSX.Element {
  const [pairsText, setPairsText] = useState('');
  const [starting, setStarting] = useState(false);
  const [runs, setRuns] = useState<RunInfo[]>([]);
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ run: RunInfo; items: RunItem[] } | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const detailPollRef = useRef<number | null>(null);

  const showToast = useCallback((type: Toast['type'], message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 6000);
  }, []);

  const parsedCount = useMemo(() => countPairs(pairsText), [pairsText]);

  const refreshRuns = useCallback(async (): Promise<void> => {
    try {
      const r = await api<{ runs: RunInfo[] }>('/api/domain-add-runs?limit=15');
      setRuns(r.runs);
    } catch {
      // transient — next poll retries
    }
  }, []);

  useEffect(() => {
    void refreshRuns();
  }, [refreshRuns]);

  // List poll: 3s while a run is in flight, else 7s.
  useEffect(() => {
    const hasInflight = runs.some((r) => r.status === 'queued' || r.status === 'running');
    const timer = window.setInterval(() => void refreshRuns(), hasInflight ? 3000 : 7000);
    return () => window.clearInterval(timer);
  }, [runs, refreshRuns]);

  const refreshDetail = useCallback(
    async (runId: string): Promise<void> => {
      try {
        const d = await api<{ run: RunInfo; items: RunItem[] }>(`/api/domain-add-runs/${runId}`);
        setDetail(d);
      } catch (err) {
        showToast('error', err instanceof Error ? err.message : 'Failed to load run');
      }
    },
    [showToast],
  );

  // Detail poll: 2.5s until terminal.
  useEffect(() => {
    if (detailPollRef.current !== null) {
      window.clearInterval(detailPollRef.current);
      detailPollRef.current = null;
    }
    if (!openRunId) {
      setDetail(null);
      return;
    }
    void refreshDetail(openRunId);
    detailPollRef.current = window.setInterval(() => void refreshDetail(openRunId), 2500);
    return () => {
      if (detailPollRef.current !== null) window.clearInterval(detailPollRef.current);
    };
  }, [openRunId, refreshDetail]);

  useEffect(() => {
    if (
      detail &&
      (detail.run.status === 'completed' || detail.run.status === 'failed') &&
      detailPollRef.current !== null
    ) {
      window.clearInterval(detailPollRef.current);
      detailPollRef.current = null;
    }
  }, [detail]);

  async function startRun(): Promise<void> {
    setStarting(true);
    try {
      const r = await api<CreateRunResp>('/api/domain-add-runs', {
        method: 'POST',
        body: JSON.stringify({ pairs_text: pairsText }),
      });
      const notes: string[] = [];
      if (r.skipped.length > 0) {
        const byReason = new Map<string, number>();
        for (const s of r.skipped) byReason.set(s.reason, (byReason.get(s.reason) ?? 0) + 1);
        notes.push(
          `skipped ${r.skipped.length} (${[...byReason].map(([k, v]) => `${k}: ${v}`).join(', ')})`,
        );
      }
      if (r.run_id) {
        showToast(
          'success',
          `Run started — ${r.items_enqueued.length} panel(s)${notes.length ? ` · ${notes.join(' · ')}` : ''}`,
        );
        setPairsText('');
        await refreshRuns();
        setOpenRunId(r.run_id);
      } else {
        showToast('error', `Nothing to enqueue — ${notes.join(' · ') || 'no pairs matched'}`);
      }
    } catch (err) {
      const e = err as ApiError;
      const lineErrors = (e.data as { errors?: { line: number; reason: string }[] } | undefined)
        ?.errors;
      showToast(
        'error',
        `${e.message}${
          lineErrors?.length
            ? ` — ${lineErrors
                .slice(0, 3)
                .map((le) => `line ${le.line}: ${le.reason}`)
                .join('; ')}${lineErrors.length > 3 ? '…' : ''}`
            : ''
        }`,
      );
    } finally {
      setStarting(false);
    }
  }

  async function retryErrors(runId: string): Promise<void> {
    setRetrying(true);
    try {
      const r = await api<{ retried: number }>(`/api/domain-add-runs/${runId}/retry-errors`, {
        method: 'POST',
      });
      showToast(
        r.retried > 0 ? 'success' : 'info',
        r.retried > 0 ? `Re-enqueued ${r.retried} item(s)` : 'No errored items to retry',
      );
      await refreshDetail(runId);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Retry failed');
    } finally {
      setRetrying(false);
    }
  }

  async function toggleAutoRetry(runId: string, enabled: boolean): Promise<void> {
    try {
      await api(`/api/domain-add-runs/${runId}/auto-retry`, {
        method: 'POST',
        body: JSON.stringify({ enabled }),
      });
      await refreshDetail(runId);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Toggle failed');
    }
  }

  if (openRunId && detail) {
    const { run, items } = detail;
    const terminal = run.status === 'completed' || run.status === 'failed';
    return (
      <div className="da-root">
        <div className="da-scroll">
          <div className="da-runheader">
            <button
              type="button"
              className="da-btn da-btn--ghost da-btn--small"
              onClick={() => setOpenRunId(null)}
            >
              ← Back
            </button>
            <h1 className="da-title">Domain add run</h1>
            <span className={`da-status da-status--${run.status}`}>{run.status}</span>
          </div>

          {toast && <div className={`da-toast da-toast--${toast.type}`}>{toast.message}</div>}

          <div className="da-runmeta">
            <div>
              <strong>Created</strong>
              {fmtDate(run.created_at)}
            </div>
            <div>
              <strong>Triggered by</strong>
              {run.triggered_by ?? '—'}
            </div>
          </div>

          <div className="da-runsummary">
            <span className="da-summary-pill">{run.panels_total} total</span>
            <span className="da-summary-pill da-summary-pill--ok">
              {run.panels_done - run.panels_already} added
            </span>
            <span className="da-summary-pill da-summary-pill--already">
              {run.panels_already} already
            </span>
            <span className="da-summary-pill da-summary-pill--err">
              {run.panels_errored} errored
            </span>
          </div>

          <div className="da-runactions">
            <label className="da-filter">
              <input
                type="checkbox"
                checked={run.auto_retry_enabled}
                onChange={(e) => void toggleAutoRetry(run.id, e.target.checked)}
              />
              Auto-retry errored ({run.auto_retry_attempts_used}/{run.auto_retry_attempts_cap} used)
            </label>
            <div className="da-actions-spacer" />
            <button
              type="button"
              className="da-btn da-btn--primary da-btn--small"
              onClick={() => void retryErrors(run.id)}
              disabled={retrying || !terminal || run.panels_errored === 0}
              title={terminal ? 'Reset errored items and re-enqueue them' : 'Available once the run finishes'}
            >
              {retrying ? 'Retrying…' : `Retry errors (${run.panels_errored})`}
            </button>
          </div>

          <section className="da-card">
            <div className="da-card-header">
              <h2 className="da-card-title">Panels</h2>
            </div>
            <div className="da-table-wrap">
              <table className="da-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>New domain</th>
                    <th>Source</th>
                    <th className="da-col-center">Status</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.panel_email}>
                      <td className="da-cell-mono">{it.panel_email}</td>
                      <td className="da-cell-mono">{it.new_domain}</td>
                      <td>{SOURCE_LABEL[it.source]}</td>
                      <td className="da-col-center">
                        <span className={`da-status da-status--${it.status}`}>{it.status}</span>
                      </td>
                      <td className="da-cell-error">
                        {it.error_message ? `${it.error_code ?? ''} ${it.error_message}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="da-root">
      <div className="da-scroll">
        <header className="da-header">
          <h1 className="da-title">Domain Add</h1>
          <p className="da-subtitle">
            Add + verify a replacement domain in each panel's Google Workspace. Paste one{' '}
            <code>panel_email,new_domain</code> pair per line — a worker logs into each panel,
            adds the new domain as a <strong>secondary domain</strong>, walks the manual
            verification wizard, and writes the verification TXT + Gmail MX to the domain's
            Cloudflare zone. Panels whose domain is already verified are skipped automatically.
          </p>
        </header>

        {toast && <div className={`da-toast da-toast--${toast.type}`}>{toast.message}</div>}

        <section className="da-card">
          <div className="da-card-header">
            <h2 className="da-card-title">Start a run</h2>
          </div>
          <div className="da-card-body">
            <label className="da-field">
              <span className="da-field-label">
                Email + new-domain pairs
                {parsedCount.valid > 0 && ` — ${parsedCount.valid} parsed`}
                {parsedCount.invalid > 0 && ` (${parsedCount.invalid} invalid — run will be rejected)`}
              </span>
              <textarea
                className="da-textarea da-mono"
                rows={10}
                placeholder={'admin@k2m8t4n1pv.help,k2m8t4n1pv.shop\nadmin@l9t5k7y3rw.help,l9t5k7y3rw.shop'}
                value={pairsText}
                onChange={(e) => setPairsText(e.target.value)}
                spellCheck={false}
              />
            </label>
            <div className="da-actions-row">
              <div className="da-actions-spacer" />
              <button
                type="button"
                className="da-btn da-btn--primary"
                onClick={() => void startRun()}
                disabled={starting || parsedCount.valid === 0 || parsedCount.invalid > 0}
              >
                {starting
                  ? 'Starting…'
                  : `Start run (${parsedCount.valid} panel${parsedCount.valid === 1 ? '' : 's'})`}
              </button>
            </div>
          </div>
        </section>

        <section className="da-card">
          <div className="da-card-header">
            <h2 className="da-card-title">Recent runs</h2>
          </div>
          <div className="da-table-wrap">
            <table className="da-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th className="da-col-center">Status</th>
                  <th className="da-col-num">Done</th>
                  <th className="da-col-num">Already</th>
                  <th className="da-col-num">Errored</th>
                  <th className="da-col-num">Total</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {runs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="da-empty">
                      No runs yet.
                    </td>
                  </tr>
                ) : (
                  runs.map((r) => (
                    <tr key={r.id} className="da-row--clickable" onClick={() => setOpenRunId(r.id)}>
                      <td className="da-cell-mono">{fmtDate(r.created_at)}</td>
                      <td className="da-col-center">
                        <span className={`da-status da-status--${r.status}`}>{r.status}</span>
                      </td>
                      <td className="da-col-num">{r.panels_done}</td>
                      <td className="da-col-num">{r.panels_already}</td>
                      <td className="da-col-num">{r.panels_errored}</td>
                      <td className="da-col-num">{r.panels_total}</td>
                      <td className="da-cell-faint">{r.triggered_by ?? '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
